import Surcharge from '@square/dashboard-items-data/models/surcharge';

// Functions to calculate appliedToMoney and afterApplicationTotalMoney for top level fees that takes into account
// itemizations and surcharges.
// Based off the Android implementation: https://github.com/squareup/android-register/blob/master/common/checkout-hairball/src/main/java/com/squareup/payment/TaxCalculations.java
export function calculateAppliedToAmount({
  calcItemizations,
  calcSurcharges,
  taxAndDiscountAdjuster,
  taxAdjustment
}) {
  const lineItemAdjustedItemPairs = createLineItemAdjustedItemPairs({
    calcItemizations,
    calcSurcharges,
    taxAndDiscountAdjuster
  });
  let totalAmount = getTotalTaxAmount({
    lineItemAdjustedItemPairs,
    taxAdjustment
  });

  // Find all items that the tax applies to. We do not have a more direct way to query this.
  lineItemAdjustedItemPairs.forEach(({
    adjustedItem,
    lineItem
  }) => {
    const adjustmentAmounts = adjustedItem.getTotalCollectedPerAdjustment();

    // If the cart item is not modified by the taxAdjustment, we don't care about its amount.
    if (taxAdjustment.id() in adjustmentAmounts) {
      Object.values(lineItem.appliedTaxes()).forEach(appliedTax => {
        if (appliedTax.phase() >= taxAdjustment.phase()) {
          totalAmount -= adjustmentAmounts[appliedTax.id()];
        }
      });
    }
  });
  return totalAmount;
}
export function calculateAfterApplicationItemizationsTotalAmount({
  calcItemizations,
  calcSurcharges,
  taxAndDiscountAdjuster,
  taxAdjustment
}) {
  const lineItemAdjustedItemPairs = createLineItemAdjustedItemPairs({
    calcItemizations,
    calcSurcharges,
    taxAndDiscountAdjuster
  });
  let totalAmount = getTotalTaxAmount({
    lineItemAdjustedItemPairs,
    taxAdjustment
  });

  // Find all items that the tax applies to. We do not have a more direct way to query this.
  lineItemAdjustedItemPairs.forEach(({
    adjustedItem,
    lineItem
  }) => {
    const adjustmentAmounts = adjustedItem.getTotalCollectedPerAdjustment();

    // If the cart item is not modified by the taxAdjustment, we don't care about its amount.
    if (taxAdjustment.id() in adjustmentAmounts) {
      Object.values(lineItem.appliedTaxes()).forEach(appliedTax => {
        if (appliedTax.id() !== taxAdjustment.id() && appliedTax.phase() >= taxAdjustment.phase()) {
          totalAmount -= adjustmentAmounts[appliedTax.id()];
        }
      });
    }
  });
  return totalAmount;
}
function getTotalTaxAmount({
  lineItemAdjustedItemPairs,
  taxAdjustment
}) {
  let totalAmount = 0;
  lineItemAdjustedItemPairs.forEach(({
    adjustedItem,
    lineItem
  }) => {
    let taxAppliesToItem = false;
    Object.values(lineItem.appliedTaxes()).forEach(appliedTax => {
      if (taxAdjustment.id() === appliedTax.id()) {
        taxAppliesToItem = true;
      }
    });
    if (taxAppliesToItem) {
      const itemTotal = adjustedItem.getAdjustedTotal();
      totalAmount += itemTotal;
    }
  });
  return totalAmount;
}
function createLineItemAdjustedItemPairs({
  calcItemizations,
  calcSurcharges,
  taxAndDiscountAdjuster
}) {
  return [...calcItemizations.map(lineItem => ({
    adjustedItem: taxAndDiscountAdjuster.getAdjustedItemFor(lineItem),
    lineItem
  })), ...calcSurcharges.map(lineItem => ({
    adjustedItem: taxAndDiscountAdjuster.getSurchargeAdjustedItemFor(lineItem),
    lineItem
  }))];
}

/**
 * Function that returns the unique id for a surcharge/service charge that the client should use
 * for any cart calculations.
 * This returns a clientToken (UUID) for variable service charges. A variable service charge can
 * exist multiple times within the same cart, and keying by the surcharge.id in this case would
 * disallow duplicates by only considering one of them when doing calculations.
 * Using the client-generated clientToken avoids this issue.
 *
 * For all other surcharges, use the surcharge.id to disallow duplicates or to coalesce
 * multiple of the same surcharges together.
 * Based off the backend implementation: https://github.com/squareup/java/pull/235067/files
 */
export function getSurchargeId(surchargeLineItem) {
  const calculationType = surchargeLineItem.backingDetails.surcharge.calculationType;
  if ([Surcharge.SurchargeCalculationType.VARIABLE_AMOUNT, Surcharge.SurchargeCalculationType.VARIABLE_PERCENTAGE].includes(calculationType)) {
    return surchargeLineItem.surchargeLineItemIdPair.clientToken;
  }
  return surchargeLineItem.backingDetails.surcharge.id;
}